<template>
  <div 
    class="pane-form"                      
    :class="{'is-editing': isEditing}"
  >
    <VisualTextEditor v-model:value="newPrompt"
                      data-locator="add-prompt-textarea"
                      :clean-content-on-blur="false"
                      :error-messages="newPromptErrors"
                      hide-menubar
                      :max-length="NEW_PROMPT_MAX_LENGTH"
                      :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.newPromptPlaceholder`)"
                      clean-content-on-validation
                      keep-toolbar-on-blur
                      ref="visualTextEditor"
                      @blur="onBlur"
                      @focus="onFocus"
    />
    <div class="d-flex pa-3">
      <app-button data-locator="post-prompt-button"
                  :disabled="!canPostPrompt"
                  :loading="disabled"
                  class="post-prompt-button ml-auto"
                  size="small"
                  type="icon"
                  @click="onPostPrompt"
      >
        <app-icon-ds icon="arrow-up" size="xs" color="currentColor" />
      </app-button>
    </div>
  </div>
</template>

<script>
import { escapeHtml } from '@/common/utils/strings'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'

const NEW_PROMPT_MAX_LENGTH = 5000
const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPaneForm'

export default {
  name: 'AIAssistantPaneForm',
  components: { VisualTextEditor },
  emits: ['data-unsaved','post-prompt'],
  props: {
    cleared: Boolean,
    disabled: Boolean,
  },
  data: () => ({
    NEW_PROMPT_MAX_LENGTH,
    TRANSLATION_KEY_PREFIX,
    isEditing: false,
    newPrompt: '',
  }),
  methods: {
    onClear() {
      this.$refs.visualTextEditor.clear()
    },
    onBlur() {
      this.toggleEditMode(false)
    },
    onFocus() {
      this.toggleEditMode(true)
    },
    onPostPrompt() {
      this.$emit('post-prompt', this.newPrompt)
    },
    toggleEditMode(isEditing) {
      if(this.isReadonly) return

      this.isEditing = isEditing
    },
  },
  computed: {
    canPostPrompt () {
      return this.newPromptLength > 0 && !this.newPromptIsTooLong && !this.disabled
    },
    newPromptLength () {
      return escapeHtml(this.newPrompt).length
    },
    newPromptIsTooLong () {
      return this.newPromptLength > this.NEW_PROMPT_MAX_LENGTH
    },
    newPromptErrors () {
      const errors = []

      if (this.newPromptIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
  },
  watch: {
    cleared (value) {
      if (value) {
        this.onClear()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.pane-form {
  outline: 1px solid rgb(var(--v-theme-grey-lighten-4));
  background-color: white;
  border-radius: 8px;
  overflow: hidden;

  &.is-editing {
    outline: 2px solid rgb(var(--v-theme-info-darken-1));
  }

  &.is-read-only {
    outline: 1px solid rgb(var(--v-theme-grey-200));
  }

  :deep(.editor__content .ProseMirror) {
    border: none !important;
    max-height: 100px;
  }
  :deep(.editor__menubar) {
    border: none !important;
    padding: 4px 0;
  }

  :deep(.editor__content .ProseMirror p.is-editor-empty:first-child::before) {
    color: #5E7997;
  }

  .post-prompt-button {
    border-radius: 100%;

    &:disabled {
      background-color: #EDF0F2;
      color: #5E7997;
    }
    &:not(:disabled), &:focus {    
      background-color: rgb(var(--v-theme-purple-darken-1));
      color: white !important;

      &:hover, &:active {
        background-color: rgb(var(--v-theme-purple-darken-4));
      }
    }
  }
}
</style>
